/**
 * Join Page
 *
 * Full-page flow for accepting campaign invites.
 * Handles various states: loading, invalid, not logged in, already member, valid.
 */

import { useEffect, useState } from 'react';
import { Icon } from '../Icon';
import { useInviteStore } from '../../stores/inviteStore';
import { useAuthStore } from '../../stores/authStore';
import { useCampaignStore } from '../../stores/campaignStore';
import { StormbringerIcon } from '../Ornaments';
import { MACHINA_VERSION } from '@machina/core';

interface JoinPageProps {
  code: string;
  onBack: () => void;
  onJoined: (campaignId: string) => void;
  onLogin: () => void;
  onSignup: () => void;
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

export function JoinPage({ code, onBack, onJoined, onLogin, onSignup }: JoinPageProps) {
  const user = useAuthStore((s) => s.user);
  const {
    invitePreview,
    isLoadingPreview,
    previewError,
    isAccepting,
    acceptError,
    loadInvitePreview,
    acceptInvite,
    clearPreview,
  } = useInviteStore();
  const { loadCampaigns } = useCampaignStore();

  const [joinSuccess, setJoinSuccess] = useState(false);
  const [joinedCampaignId, setJoinedCampaignId] = useState<string | null>(null);

  // Load preview on mount
  useEffect(() => {
    loadInvitePreview(code);
    return () => clearPreview();
  }, [code, loadInvitePreview, clearPreview]);

  // Handle accept
  const handleAccept = async () => {
    if (!user?.id) return;

    const result = await acceptInvite(code, user.id);
    if (result.success && result.campaignId) {
      setJoinSuccess(true);
      setJoinedCampaignId(result.campaignId);
      // Refresh campaigns list
      await loadCampaigns(user.id);

      if (result.alreadyMember) {
        // Already a member, go straight to campaign
        setTimeout(() => onJoined(result.campaignId!), 500);
      }
    }
  };

  // Handle enter campaign
  const handleEnterCampaign = () => {
    if (joinedCampaignId) {
      onJoined(joinedCampaignId);
    }
  };

  return (
    <div style={styles.container}>
      {/* Background pattern */}
      <div style={styles.backgroundPattern} />

      {/* Card */}
      <div style={styles.card}>
        {/* Header */}
        <div style={styles.header}>
          <StormbringerIcon size={40} />
          <div>
            <h1 style={styles.title}>MACHINA</h1>
            <p style={styles.subtitle}>Campaign Invitation</p>
          </div>
        </div>

        {/* Divider */}
        <div style={styles.divider} />

        {/* Content based on state */}
        <div style={styles.content}>
          {/* Loading */}
          {isLoadingPreview && (
            <div style={styles.centeredContent}>
              <Icon name="slow" size={48} color={GOLD_DIM} />
              <p style={styles.loadingText}>Loading invitation...</p>
            </div>
          )}

          {/* Error */}
          {previewError && !isLoadingPreview && (
            <div style={styles.centeredContent}>
              <Icon name="alert" size={48} color="#f87171" />
              <h3 style={styles.errorTitle}>Something went wrong</h3>
              <p style={styles.errorText}>{previewError}</p>
              <button onClick={onBack} style={styles.backButton}>
                <Icon name="chevron-left" size={16} />
                Return to Home
              </button>
            </div>
          )}

          {/* Invalid invite */}
          {invitePreview && !invitePreview.valid && !isLoadingPreview && (
            <div style={styles.centeredContent}>
              <Icon name="close" size={48} color="#f87171" />
              <h3 style={styles.errorTitle}>Invalid Invitation</h3>
              <p style={styles.errorText}>{invitePreview.reason}</p>
              <button onClick={onBack} style={styles.backButton}>
                <Icon name="chevron-left" size={16} />
                Return to Home
              </button>
            </div>
          )}

          {/* Success state */}
          {joinSuccess && invitePreview?.valid && (
            <div style={styles.centeredContent}>
              <Icon name="check" size={48} color="#4ade80" />
              <h3 style={styles.successTitle}>Welcome to the Party!</h3>
              <p style={styles.successText}>
                You've joined <strong>{invitePreview.campaignName}</strong>
              </p>
              <button onClick={handleEnterCampaign} style={styles.primaryButton}>
                <Icon name="play" size={16} />
                Enter Campaign
              </button>
            </div>
          )}

          {/* Valid invite - not logged in */}
          {invitePreview?.valid && !user && !isLoadingPreview && !joinSuccess && (
            <div>
              <CampaignPreview
                name={invitePreview.campaignName!}
                description={invitePreview.campaignDescription}
                dmName={invitePreview.dmName!}
                playerCount={invitePreview.playerCount!}
              />

              <div style={styles.authPrompt}>
                <Icon name="lock" size={20} color={GOLD} />
                <p style={styles.authText}>
                  Sign in or create an account to join this campaign
                </p>
              </div>

              <div style={styles.authButtons}>
                <button onClick={onLogin} style={styles.secondaryButton}>
                  Sign In
                </button>
                <button onClick={onSignup} style={styles.primaryButton}>
                  Create Account
                </button>
              </div>
            </div>
          )}

          {/* Valid invite - logged in */}
          {invitePreview?.valid && user && !isLoadingPreview && !joinSuccess && (
            <div>
              <CampaignPreview
                name={invitePreview.campaignName!}
                description={invitePreview.campaignDescription}
                dmName={invitePreview.dmName!}
                playerCount={invitePreview.playerCount!}
              />

              {/* Accept error */}
              {acceptError && (
                <div style={styles.acceptError}>
                  <Icon name="alert" size={16} color="#f87171" />
                  {acceptError}
                </div>
              )}

              <button
                onClick={handleAccept}
                disabled={isAccepting}
                style={{
                  ...styles.primaryButton,
                  opacity: isAccepting ? 0.6 : 1,
                  cursor: isAccepting ? 'not-allowed' : 'pointer',
                  width: '100%',
                }}
              >
                {isAccepting ? (
                  <>
                    <Icon name="slow" size={16} />
                    Joining...
                  </>
                ) : (
                  <>
                    <Icon name="token" size={16} />
                    Join Campaign
                  </>
                )}
              </button>

              <button onClick={onBack} style={styles.backLink}>
                <Icon name="chevron-left" size={14} />
                Back to Campaign List
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Version */}
      <p style={styles.version}>v{MACHINA_VERSION}</p>
    </div>
  );
}

// Campaign Preview Component
interface CampaignPreviewProps {
  name: string;
  description: string | null | undefined;
  dmName: string;
  playerCount: number;
}

function CampaignPreview({ name, description, dmName, playerCount }: CampaignPreviewProps) {
  return (
    <div style={styles.preview}>
      <h2 style={styles.campaignName}>{name}</h2>

      {description && (
        <p style={styles.campaignDescription}>{description}</p>
      )}

      <div style={styles.campaignMeta}>
        <div style={styles.metaItem}>
          <Icon name="crown" size={14} color={GOLD} />
          <span>DM: {dmName}</span>
        </div>
        <div style={styles.metaItem}>
          <Icon name="token" size={14} color="var(--color-text-muted)" />
          <span>{playerCount} {playerCount === 1 ? 'player' : 'players'}</span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--color-background)',
    padding: 'var(--spacing-lg)',
    position: 'relative',
    overflow: 'hidden',
  },

  backgroundPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.03,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
  },

  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-lg)',
    border: `2px solid ${GOLD_DIM}`,
    boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 60px rgba(212, 175, 55, 0.1)`,
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    padding: 'var(--spacing-lg)',
    backgroundColor: 'var(--color-background)',
  },

  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.2em',
    color: GOLD,
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  subtitle: {
    margin: 0,
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    letterSpacing: '0.1em',
  },

  divider: {
    height: 1,
    background: `linear-gradient(90deg, transparent, ${GOLD_DIM}, transparent)`,
  },

  content: {
    padding: 'var(--spacing-xl)',
  },

  centeredContent: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 'var(--spacing-md)',
    padding: 'var(--spacing-lg) 0',
  },

  loadingText: {
    margin: 0,
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
  },

  errorTitle: {
    margin: 0,
    color: '#f87171',
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
  },

  errorText: {
    margin: 0,
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
  },

  successTitle: {
    margin: 0,
    color: '#4ade80',
    fontSize: 'var(--font-size-lg)',
    fontFamily: 'var(--font-display)',
  },

  successText: {
    margin: 0,
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
  },

  preview: {
    padding: 'var(--spacing-lg)',
    backgroundColor: 'var(--color-background)',
    borderRadius: 'var(--radius-md)',
    border: `1px solid ${GOLD_DIM}`,
    marginBottom: 'var(--spacing-lg)',
  },

  campaignName: {
    margin: 0,
    marginBottom: 'var(--spacing-sm)',
    fontSize: 'var(--font-size-xl)',
    fontFamily: 'var(--font-display)',
    color: GOLD,
  },

  campaignDescription: {
    margin: 0,
    marginBottom: 'var(--spacing-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
    lineHeight: 1.5,
  },

  campaignMeta: {
    display: 'flex',
    gap: 'var(--spacing-lg)',
    paddingTop: 'var(--spacing-sm)',
    borderTop: '1px solid var(--color-border)',
  },

  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-muted)',
  },

  authPrompt: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'rgba(212, 175, 55, 0.1)',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 'var(--spacing-md)',
  },

  authText: {
    margin: 0,
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-secondary)',
  },

  authButtons: {
    display: 'flex',
    gap: 'var(--spacing-sm)',
  },

  primaryButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    background: `linear-gradient(180deg, ${GOLD} 0%, #a08030 50%, #8b6914 100%)`,
    border: `2px solid ${GOLD}`,
    borderRadius: 'var(--radius-sm)',
    color: '#1a1410',
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
  },

  secondaryButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'transparent',
    border: `1px solid var(--color-border)`,
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-sm) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: `1px solid var(--color-border)`,
    borderRadius: 'var(--radius-sm)',
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    marginTop: 'var(--spacing-md)',
  },

  backLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-xs)',
    width: '100%',
    marginTop: 'var(--spacing-md)',
    padding: 'var(--spacing-sm)',
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--color-text-muted)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
  },

  acceptError: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
    padding: 'var(--spacing-md)',
    backgroundColor: 'rgba(248, 113, 113, 0.1)',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 'var(--spacing-md)',
    color: '#f87171',
    fontSize: 'var(--font-size-sm)',
  },

  version: {
    marginTop: 'var(--spacing-lg)',
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
  },
};
